import React from 'react';
import { Cloud, Beaker, Database, Lock, Server, Key, Code, Cpu, ArrowRight, BookOpen, FlaskConical, GitBranch, Github, ExternalLink } from 'lucide-react';
import logoIcon from './assets/logoIcon.png';
import textLogo from './assets/Text-Logo.png';

const Labs = () => {
  return (
    <div className="min-h-screen relative overflow-hidden bg-[#4573B9]">
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#282F4B] via-[#4573B9] to-[#4573B9] opacity-90" />

      {/* Magical background elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Animated gradient orbs */}
        <div 
          className="absolute w-[800px] h-[800px] rounded-full blur-3xl opacity-30 animate-float"
          style={{
            background: 'linear-gradient(45deg, rgba(125, 176, 210, 0.6), rgba(69, 115, 185, 0.4))',
            top: '-20%',
            right: '-10%',
          }}
        />
        <div 
          className="absolute w-[600px] h-[600px] rounded-full blur-3xl opacity-20 animate-float-reverse"
          style={{
            background: 'linear-gradient(45deg, rgba(40, 47, 75, 0.4), rgba(125, 176, 210, 0.3))',
            bottom: '-10%',
            left: '-10%',
          }}
        />
        
        {/* Shimmering overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ0cmFuc3BhcmVudCIvPjxwYXRoIGQ9Ik0wIDAgTDIwMCAyMDBNMjAwIDAgTDAgMjAwIiBzdHJva2U9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvc3ZnPg==')] opacity-20 animate-shimmer"></div>
        
        {/* Floating particles */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full animate-float-particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                opacity: 0.2
              }}
            />
          ))}
        </div>
      </div>

      <div className="relative px-4 py-16 sm:py-24">
        <div className="max-w-5xl mx-auto">
          {/* Header with Logo */}
          <div className="flex justify-center mb-12">
            <div className="flex items-center gap-2">
              <img 
                src={logoIcon} 
                alt="TinyCloud Icon"
                className="h-10 w-10 object-contain"
              />
              <img 
                src={textLogo}
                alt="TinyCloud"
                className="h-6 object-contain"
              />
            </div>
          </div>

          {/* Main Title */}
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-[#FDF9D2] mb-6">
              Labs
            </h1>
            <p className="text-xl text-[#FDF9D2]/90 max-w-3xl mx-auto">
              TinyCloud Labs is building the sovereign data layer that gives you control of your digital self. Everything we are exploring furthers our mission of a world where your data works for you and your interests, not corporations, governments, or superintelligent AI.
            </p>
          </div>

          {/* Chat with Sam Section */}
          <div className="mb-16 text-center">
            <div className="bg-[#282F4B]/20 backdrop-blur-sm rounded-xl p-6 border border-[#7DB0D2]/30 hover:border-[#7DB0D2]/60 transition-all duration-300 max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold text-[#FDF9D2] mb-4">
                Interested in Our Projects?
              </h3>
              <p className="text-[#FDF9D2]/80 mb-6">
                Want to learn more about what we're building or explore opportunities to work together? Schedule a chat with Sam, our founder.
              </p>
              <a 
                href="https://tinycloud.xyz/meet-sam" 
                className="inline-flex items-center px-6 py-3 bg-[#7DB0D2] text-[#282F4B] font-bold rounded-lg hover:bg-[#7DB0D2]/90 transition-all"
                target="_blank"
                rel="noopener noreferrer"
              >
                Chat with Sam
                <ArrowRight className="h-5 w-5 ml-2" />
              </a>
            </div>
          </div>

          {/* Products Section */}
          <div className="mb-20">
            <div className="flex items-center mb-8">
              <h2 className="text-3xl font-bold text-[#FDF9D2]">Products</h2>
              <div className="h-px bg-[#7DB0D2]/30 flex-grow ml-4"></div>
            </div>
            <p className="text-[#FDF9D2]/80 mb-10">
              Revenue Drivers that solve specific problems for individuals.
            </p>

            {/* Product Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Muse Card */}
              <div className="bg-[#282F4B]/20 backdrop-blur-sm rounded-xl p-6 border border-[#7DB0D2]/30 hover:border-[#7DB0D2]/60 transition-all duration-300 group">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-[#FDF9D2] group-hover:text-[#7DB0D2] transition-all">
                    <a href="/muse" className="hover:underline flex items-center">
                      Muse <ArrowRight className="h-5 w-5 ml-2 opacity-0 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all" />
                    </a>
                  </h3>
                </div>
                <div className="mb-4">
                  <a 
                    href="https://muse.tinycloud.xyz/" 
                    className="text-sm inline-flex items-center px-3 py-1 bg-[#7DB0D2]/20 text-[#7DB0D2] rounded-full mr-2 hover:bg-[#7DB0D2]/30"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Web App <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                  <a 
                    href="/muse" 
                    className="text-sm inline-flex items-center px-3 py-1 bg-[#7DB0D2]/20 text-[#7DB0D2] rounded-full hover:bg-[#7DB0D2]/30"
                  >
                    Mobile App <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                </div>
                <p className="text-[#FDF9D2]/80 mb-4">
                  Muse is an AI assistant for capturing and retrieving your memories and knowledge, seamlessly integrated with your data. A work in progress, it's our entrypoint into the consumer market. Our goal with Muse it to make an AI product that is easy to use to understand everything about your life.
                </p>
                <p className="text-[#FDF9D2]/70 text-sm">
                  Muse is uses a combination of traditional web2 technologies and TinyCloud protocol to securely store and retrieve your data.
                </p>
              </div>

              {/* Wikiforge Card */}
              <div className="bg-[#282F4B]/20 backdrop-blur-sm rounded-xl p-6 border border-[#7DB0D2]/30 hover:border-[#7DB0D2]/60 transition-all duration-300 group">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-[#FDF9D2] group-hover:text-[#7DB0D2] transition-all">
                    <a 
                      href="https://autograph-web.pages.dev/" 
                      className="hover:underline flex items-center"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Wikiforge <ArrowRight className="h-5 w-5 ml-2 opacity-0 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all" />
                    </a>
                  </h3>
                </div>
                <div className="mb-4">
                  <a 
                    href="https://autograph-web.pages.dev/" 
                    className="text-sm inline-flex items-center px-3 py-1 bg-[#7DB0D2]/20 text-[#7DB0D2] rounded-full hover:bg-[#7DB0D2]/30"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Product Mockup <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                </div>
                <p className="text-[#FDF9D2]/80 mb-4">
                  Wikiforge is a tool to automaitcally build and maintain obsidian stlyed, markdwon knwoledge graphs. Wikiforge is built on top of AutoGraph, a technology developed by TinyCloud Labs.
                </p>
                <p className="text-[#FDF9D2]/70 text-sm">
                  These graphs functions like powerful wiki's creating and updating articles based on entities and relations extracted from user submissions.
                </p>
              </div>
            </div>
          </div>

          {/* Technologies Section */}
          <div className="mb-20">
            <div className="flex items-center mb-8">
              <h2 className="text-3xl font-bold text-[#FDF9D2]">Technologies</h2>
              <div className="h-px bg-[#7DB0D2]/30 flex-grow ml-4"></div>
            </div>
            <p className="text-[#FDF9D2]/80 mb-10">
              Novel capabilities that we've developed here at TinyCloud Labs.
            </p>

            {/* Technology Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* TinyCloud Protocol Card */}
              <div className="bg-[#282F4B]/20 backdrop-blur-sm rounded-xl p-6 border border-[#7DB0D2]/30 hover:border-[#7DB0D2]/60 transition-all duration-300 group">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-[#FDF9D2] group-hover:text-[#7DB0D2] transition-all">
                    <a 
                      href="https://github.com/tinycloudlabs/tinycloud-protocol" 
                      className="hover:underline flex items-center"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      TinyCloud Protocol <ArrowRight className="h-5 w-5 ml-2 opacity-0 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all" />
                    </a>
                  </h3>
                </div>
                <div className="mb-4 flex flex-wrap gap-2">
                  <a 
                    href="/litepaper" 
                    className="text-sm inline-flex items-center px-3 py-1 bg-[#7DB0D2]/20 text-[#7DB0D2] rounded-full hover:bg-[#7DB0D2]/30"
                  >
                    Litepaper
                  </a>
                  <a 
                    href="http://demo.tinycloud.xyz" 
                    className="text-sm inline-flex items-center px-3 py-1 bg-[#7DB0D2]/20 text-[#7DB0D2] rounded-full hover:bg-[#7DB0D2]/30"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Protocol Demo <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                  <a 
                    href="https://github.com/tinycloudlabs/tinycloud-node" 
                    className="text-sm inline-flex items-center px-3 py-1 bg-[#7DB0D2]/20 text-[#7DB0D2] rounded-full hover:bg-[#7DB0D2]/30"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Protocol GitHub <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                  <a 
                    href="https://docs.tinycloud.xyz" 
                    className="text-sm inline-flex items-center px-3 py-1 bg-[#7DB0D2]/20 text-[#7DB0D2] rounded-full hover:bg-[#7DB0D2]/30"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Protocol SDK Docs <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                </div>
                <p className="text-[#FDF9D2]/80">
                  A decentralized protocol for storing and computing on sovereign data, enabling truly user-controlled cloud services. The protocol lets users run their own TinyCloud nodes, permission who has access via signing, and to computations on their data via the protocol.
                </p>
              </div>

              {/* Autograph Card */}
              <div className="bg-[#282F4B]/20 backdrop-blur-sm rounded-xl p-6 border border-[#7DB0D2]/30 hover:border-[#7DB0D2]/60 transition-all duration-300 group">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-[#FDF9D2] group-hover:text-[#7DB0D2] transition-all">
                    <a 
                      href="/autograph" 
                      className="hover:underline flex items-center"
                    >
                      Autograph <ArrowRight className="h-5 w-5 ml-2 opacity-0 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all" />
                    </a>
                  </h3>
                </div>
                <div className="mb-4">
                  <a 
                    href="https://autograph-web.pages.dev/" 
                    className="text-sm inline-flex items-center px-3 py-1 bg-[#7DB0D2]/20 text-[#7DB0D2] rounded-full hover:bg-[#7DB0D2]/30"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Product Mockup <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                </div>
                <p className="text-[#FDF9D2]/80">
                  Autograph is a powerful tool for creating and maintaining human readable, markdown based knowledge graphs. Build to be compatible with the TinyCloud Obsidian, Autograph takes in any unstructured data, identifies entities and relations, and builds/maintains knowledge graphs from the new data. Autograph is currently closed-source, are we are making it available via API and the WikiForge product.
                </p>
              </div>
            </div>
          </div>

          {/* Experiments Section */}
          <div className="mb-12">
            <div className="flex items-center mb-8">
              <h2 className="text-3xl font-bold text-[#FDF9D2]">Experiments</h2>
              <div className="h-px bg-[#7DB0D2]/30 flex-grow ml-4"></div>
            </div>
            <p className="text-[#FDF9D2]/80 mb-10">
              Explorations into new capabilities and technologies that demonstrate the capabilities of the technologies we've built.
            </p>

            {/* Experiment Cards */}
            <div className="grid grid-cols-1 gap-8">
              {/* Location Experiments Card */}
              <div className="bg-[#282F4B]/20 backdrop-blur-sm rounded-xl p-6 border border-[#7DB0D2]/30 hover:border-[#7DB0D2]/60 transition-all duration-300">
                <h3 className="text-2xl font-bold text-[#FDF9D2] mb-4">
                    Location Experiments
                </h3>
                <blockquote className="border-l-4 border-[#7DB0D2]/50 pl-4 py-1 mb-4 italic text-[#FDF9D2]/80">
                  Testing privacy-preserving location streaming and access control over geospatial data. Building 3 apps to demonstrate the capabilities of TinyCloud protocol. Granting the ability to do a computation on your location, instead of 0 or 100% of your location, enables a new class of applications and services.
                </blockquote>
              </div>

              {/* Private Set Intersection Card */}
              <div className="bg-[#282F4B]/20 backdrop-blur-sm rounded-xl p-6 border border-[#7DB0D2]/30 hover:border-[#7DB0D2]/60 transition-all duration-300">
                <h3 className="text-2xl font-bold text-[#FDF9D2] mb-4">
                  
                    Private Set Intersection
                </h3>
                <div className="mb-4">
                  <a 
                    href="https://psi-demo.pages.dev/" 
                    className="text-sm inline-flex items-center px-3 py-1 bg-[#7DB0D2]/20 text-[#7DB0D2] rounded-full hover:bg-[#7DB0D2]/30"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Demo <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                </div>
                <blockquote className="border-l-4 border-[#7DB0D2]/50 pl-4 py-1 mb-4 italic text-[#FDF9D2]/80">
                  We are exploring the limits of personal data, given a set of privacy preserving technologies. Exploring cryptographic protocols that allow two parties to compute intersections of their datasets without revealing extra information. Keeping this data private, adding additional verifiable information, and being able to do do can enable verifiable, high trust interactions (like directly paying someone to advertise to them because they verifiably match a criteria). Checkout our <a href="https://psi-demo.pages.dev/" className="text-[#7DB0D2] hover:underline" target="_blank" rel="noopener noreferrer">Never have I Ever PSI Demo</a> to see a simple example of this in action.
                </blockquote>
              </div>

              {/* Community Wiki Tools Card */}
              <div className="bg-[#282F4B]/20 backdrop-blur-sm rounded-xl p-6 border border-[#7DB0D2]/30 hover:border-[#7DB0D2]/60 transition-all duration-300">
                <h3 className="text-2xl font-bold text-[#FDF9D2] mb-4">
                  
                    Community Wiki Tools
                </h3>
                <div className="mb-4">
                  <a 
                    href="https://aleph.communitywiki.ai/" 
                    className="text-sm inline-flex items-center px-3 py-1 bg-[#7DB0D2]/20 text-[#7DB0D2] rounded-full hover:bg-[#7DB0D2]/30 mr-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Aleph Demo <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                  <a 
                    href="https://github.com/TinyCloudLabs/Aleph-Community-Wiki" 
                    className="text-sm inline-flex items-center px-3 py-1 bg-[#7DB0D2]/20 text-[#7DB0D2] rounded-full hover:bg-[#7DB0D2]/30"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Github <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                </div>
                <blockquote className="border-l-4 border-[#7DB0D2]/50 pl-4 py-1 italic text-[#FDF9D2]/80">
                  The Community Wiki tool was an experiment to use Autograph to build a community wiki. The goal of the wiki was to provide insight into the ongoings of a popup community in an interpretable way.
                </blockquote>
              </div>
            </div>
          </div>

          {/* Back to Home Button */}
          <div className="text-center mt-16">
            <a 
              href="/" 
              className="inline-flex items-center px-6 py-3 bg-[#7DB0D2] text-[#282F4B] font-bold rounded-lg hover:bg-[#7DB0D2]/90 transition-all"
            >
              Back to Home
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Labs;